import React, { useState, useMemo, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Brain, 
  Lightbulb, 
  Network, 
  TrendingDown, 
  TrendingUp, 
  Filter,
  BarChart3,
  Users,
  FileText,
  RefreshCw,
  Info,
  PenTool,
  MessageSquare,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAiAnalysisData, UseAiAnalysisDataOptions } from '@/modules/app/hooks';
import { emergencyFilterWorkspaceData, secureLog, safeLog } from '@/modules/app/security';
import { useContentWritingWebhook } from '@/modules/app/ai-analysis/hooks/useContentWritingWebhook';

interface AiAnalysisTabsProps {
  workspaceId: string;
  className?: string;
}

/**
 * Keyword Categorization Tab Content
 */
const KeywordCategorizationTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean;
  onRefresh: () => void;
  workspaceId: string;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, workspaceId, onStartAnalysis, analysisStatus }) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [intentFilter, setIntentFilter] = useState('all');

  const filteredData = useMemo(() => {
    // First apply emergency workspace filtering
    const emergencyFiltered = emergencyFilterWorkspaceData(data, workspaceId, {
      logFiltering: true,
      fieldName: 'keyword_categorization_ai'
    });
    
    return emergencyFiltered.filter(item => {
      const matchesSearch = !searchFilter || 
        item.keyword_text?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchFilter.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const matchesIntent = intentFilter === 'all' || item.search_intent_classification === intentFilter;
      
      return matchesSearch && matchesCategory && matchesIntent;
    });
  }, [data, searchFilter, categoryFilter, intentFilter, workspaceId]);

  const categories = useMemo(() => 
    [...new Set(data.map(item => item.category).filter(Boolean))], [data]
  );

  const intents = useMemo(() => 
    [...new Set(data.map(item => item.search_intent_classification).filter(Boolean))], [data]
  );

  const stats = useMemo(() => {
    const avgPriority = data.length > 0 
      ? data.reduce((sum, item) => sum + (item.priority_score || 0), 0) / data.length
      : 0;
    const highValueCount = data.filter(item => item.business_value === 'high').length;
    const quickWins = data.filter(item => 
      item.priority_score >= 80 && item.business_value === 'high'
    ).length;

    return { avgPriority, highValueCount, quickWins };
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <BarChart3 className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Priority</p>
              <p className="text-xl font-medium">{stats.avgPriority.toFixed(1)}</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <TrendingUp className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">High Value</p>
              <p className="text-xl font-medium">{stats.highValueCount}</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <Brain className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Quick Wins</p>
              <p className="text-xl font-medium">{stats.quickWins}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="flex-1">
          <Input
            placeholder="Search keywords or categories..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full border-border/50 focus:border-border"
          />
        </div>
        
        <div className="flex gap-2">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px] border-border/50">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(category => (
              <SelectItem key={category} value={category}>{category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={intentFilter} onValueChange={setIntentFilter}>
          <SelectTrigger className="w-[180px] border-border/50">
            <SelectValue placeholder="Intent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Intents</SelectItem>
            {intents.map(intent => (
              <SelectItem key={intent} value={intent}>{intent}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        </div>
      </div>

      {/* Data Table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Keyword Categorization Results
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              AI-generated keyword categories with priority scores and business value assessment
            </p>
          </div>
          <Badge variant="secondary">{filteredData.length} keywords</Badge>
        </div>
        
        {filteredData.length > 0 ? (
          <table className="w-full border-separate border-spacing-0 table-auto min-w-[1400px]">
            <thead>
              <tr className="border-b border-border/30">
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Keyword</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Category</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 w-96">Category Reasoning</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Priority</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Intent</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Business Value</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Volume</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Difficulty</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">Competition</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30">CPC</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={index} className="border-b border-border/20 hover:bg-muted/20 transition-colors duration-150">
                  <td className="px-3 py-3 text-sm text-foreground font-medium break-words whitespace-normal">
                    <div className="break-words whitespace-normal">{item.keyword_text}</div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <Badge variant="outline">{item.category}</Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground break-words whitespace-normal">
                    <div className="break-words whitespace-normal" title={item.category_reasoning}>
                      {item.category_reasoning || 'No reasoning available'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="flex items-center gap-2">
                      <div 
                        className={`w-2 h-2 rounded-full ${
                          item.priority_score >= 80 ? 'bg-green-500' :
                          item.priority_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`} 
                      />
                      <span>{item.priority_score}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <Badge variant="secondary">{item.search_intent_classification}</Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <Badge 
                      variant={
                        item.business_value === 'Very Important' ? 'default' :
                        item.business_value === 'Important' ? 'secondary' : 
                        item.business_value === 'high' ? 'default' :
                        item.business_value === 'medium' ? 'secondary' : 'outline'
                      }
                    >
                      {item.business_value}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">{item.search_volume?.toLocaleString() || item.msv?.toLocaleString() || 'N/A'}</td>
                  <td className="px-3 py-3 text-sm text-foreground font-medium">
                    {item.kw_difficulty ? Number(item.kw_difficulty).toFixed(0) : '-'}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {item.competition ? (
                      <Badge 
                        variant="outline"
                        className={
                          item.competition === 'LOW' ? 'bg-green-50 text-green-700 border-green-200' :
                          item.competition === 'MEDIUM' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                          item.competition === 'HIGH' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }
                      >
                        {item.competition}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground font-medium">
                    {item.cpc ? `$${Number(item.cpc).toFixed(2)}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
              </table>        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm mb-4">No keyword categorization data available yet.</p>
            <p className="text-xs mb-6">Start an AI analysis to see categorized keywords.</p>
            {onStartAnalysis && !analysisStatus?.isRunning && (
              <Button
                onClick={onStartAnalysis}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
              >
                <Brain className="h-4 w-4 mr-2" />
                Start AI Analysis
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Content Ideas Tab Content - Enhanced collapsible hierarchical pillar structure
 */
const ContentIdeasTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean; 
  onRefresh: () => void; 
  workspaceId: string;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, workspaceId, onStartAnalysis, analysisStatus }) => {
  const { triggerContentWriting, isLoading: isWritingContent } = useContentWritingWebhook();
  
  // State for tracking expanded pillar pages
  const [expandedPillars, setExpandedPillars] = useState<Set<string>>(new Set());
  
  const filteredData = useMemo(() => {
    // Database queries already handle user_id, workspace_id filtering
    return data || [];
  }, [data, workspaceId]);

  const handleWriteContent = async (idea: any) => {
    try {
      await triggerContentWriting(idea, workspaceId);
    } catch (error) {
      console.error('Failed to trigger content writing:', error);
    }
  };

  // Enhanced pillar group structure
  interface PillarGroup {
    pillar: any;
    childContent: any[];
    isExpanded: boolean;
    childCount: number;
    pillarId: string;
  }

  // Enhanced hierarchy building for collapsible structure
  const buildPillarGroups = (items: any[]): { pillarGroups: PillarGroup[], standaloneContent: any[] } => {
    const pillarPosts = items.filter(item => 
      item.article_type === 'Pillar' || item.article_type === 'Pillar Page'
    );
    const supportingContent = items.filter(item => 
      item.article_type !== 'Pillar' && item.article_type !== 'Pillar Page'
    );
    
    // Group supporting content by cluster_name
    const contentByCluster = supportingContent.reduce((acc, item) => {
      const clusterKey = item.cluster_name || 'Uncategorized';
      if (!acc[clusterKey]) {
        acc[clusterKey] = [];
      }
      acc[clusterKey].push(item);
      return acc;
    }, {} as Record<string, any[]>);
    
    const pillarGroups: PillarGroup[] = [];
    
    // Create pillar groups with their supporting content
    pillarPosts.forEach(pillar => {
      const pillarId = pillar.id || `pillar-${pillar.title}-${pillar.cluster_name}`;
      const clusterKey = pillar.cluster_name || 'Uncategorized';
      const childContent = contentByCluster[clusterKey] || [];
      
      pillarGroups.push({
        pillar,
        childContent,
        isExpanded: expandedPillars.has(pillarId),
        childCount: childContent.length,
        pillarId
      });
      
      // Remove processed items to avoid duplication
      if (contentByCluster[clusterKey]) {
        delete contentByCluster[clusterKey];
      }
    });
    
    // Remaining supporting content as standalone items
    const standaloneContent: any[] = [];
    Object.entries(contentByCluster).forEach(([clusterName, items]) => {
      (items as any[]).forEach(item => {
        standaloneContent.push(item);
      });
    });
    
    return { pillarGroups, standaloneContent };
  };

  const { pillarGroups, standaloneContent } = useMemo(() => {
    return buildPillarGroups(filteredData);
  }, [filteredData, expandedPillars]);

  // Toggle expansion for a specific pillar
  const togglePillarExpansion = (pillarId: string) => {
    setExpandedPillars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pillarId)) {
        newSet.delete(pillarId);
      } else {
        newSet.add(pillarId);
      }
      return newSet;
    });
  };

  // Global expand/collapse functions
  const expandAllPillars = () => {
    const allPillarIds = pillarGroups.map(group => group.pillarId);
    setExpandedPillars(new Set(allPillarIds));
  };

  const collapseAllPillars = () => {
    setExpandedPillars(new Set());
  };

  // Enhanced column definitions with responsive widths
  const getContentColumns = () => [
    { key: 'title', label: 'Title & Description', width: 'min-w-[500px] w-[30%]' },
    { key: 'primary_keyword', label: 'Primary Keyword', width: 'min-w-[160px] w-[12%]' },
    { key: 'secondary_keywords', label: 'Secondary Keywords', width: 'min-w-[250px] w-[15%]' },
    { key: 'category', label: 'Category', width: 'min-w-[100px] w-[8%]' },
    { key: 'status', label: 'Status', width: 'min-w-[100px] w-[8%]' },
    { key: 'actions', label: 'Actions', width: 'min-w-[140px] w-[10%]' },
    { key: 'content_type', label: 'Content Type', width: 'min-w-[140px] w-[10%]' },
    { key: 'article_type', label: 'Article Type', width: 'min-w-[120px] w-[8%]' },
    { key: 'cluster_name', label: 'Cluster Name', width: 'min-w-[160px] w-[12%]' },
    { key: 'content_focus', label: 'Content Focus', width: 'min-w-[350px] w-[20%]' },
    { key: 'estimated_word_count', label: 'Word Count', width: 'min-w-[100px] w-[7%]' },
    { key: 'keyword_integration_notes', label: 'Integration Notes', width: 'min-w-[300px] w-[18%]' },
    { key: 'related_terms', label: 'Related Terms', width: 'min-w-[250px] w-[15%]' },
    { key: 'detected_industry', label: 'Industry', width: 'min-w-[120px] w-[8%]' },
    { key: 'url', label: 'URL', width: 'min-w-[200px] w-[12%]' },
    { key: 'publish_date', label: 'Publish Date', width: 'min-w-[120px] w-[8%]' },
    { key: 'created_at', label: 'Created', width: 'min-w-[120px] w-[8%]' }
  ];

  // Enhanced cell renderer for hierarchical display with expand/collapse
  const contentCellRenderer = (value: any, column: any, row: any, isPillar: boolean = false, isChild: boolean = false, pillarId?: string, childCount?: number) => {
    switch (column.key) {
      case 'title':
        if (isPillar) {
          const isExpanded = expandedPillars.has(pillarId || '');
          return (
            <div className="flex items-start gap-2">
              <button
                onClick={() => togglePillarExpansion(pillarId || '')}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors mt-1"
                disabled={childCount === 0}
              >
                {childCount > 0 ? (
                  isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )
                ) : (
                  <div className="w-4 h-4" />
                )}
                <span className="text-blue-600 text-lg">🏛️</span>
              </button>
              <div className="flex-1">
                <div className="text-foreground leading-normal font-semibold">
                  {row.title}
                  {childCount > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      ({isExpanded ? 'Hide' : 'Show'} {childCount} page{childCount !== 1 ? 's' : ''})
                    </span>
                  )}
                </div>
                {row.description && (
                  <div className="text-xs text-muted-foreground mt-1 break-words whitespace-normal">
                    {row.description}
                  </div>
                )}
              </div>
            </div>
          );
        } else if (isChild) {
          return (
            <div className="flex items-start gap-2 ml-6">
              <span className="text-gray-400 mt-1">└─</span>
              <div className="flex-1">
                <div className="text-foreground leading-normal">
                  {row.title}
                </div>
                {row.description && (
                  <div className="text-xs text-muted-foreground mt-1 break-words whitespace-normal">
                    {row.description}
                  </div>
                )}
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex items-start gap-2">
              <span className="text-gray-500 text-lg">📄</span>
              <div className="flex-1">
                <div className="text-foreground leading-normal">
                  {row.title}
                </div>
                {row.description && (
                  <div className="text-xs text-muted-foreground mt-1 break-words whitespace-normal">
                    {row.description}
                  </div>
                )}
              </div>
            </div>
          );
        }
      case 'actions':
        return (
          <Button
            onClick={() => handleWriteContent(row)}
            disabled={isWritingContent}
            size="sm"
          >
            <PenTool className="h-4 w-4 mr-1.5" />
            {isWritingContent ? 'Writing...' : 'Write Content'}
          </Button>
        );
      case 'primary_keyword':
        return value ? (
          <Badge variant="outline" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'category':
        return value ? (
          <Badge variant="secondary" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'content_type':
        return value ? (
          <Badge variant="outline" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'article_type':
        return value ? (
          <Badge variant={value.includes('Pillar') ? 'default' : 'secondary'} className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'status':
        return (
          <Badge variant="outline" className="text-xs">
            {value || 'TBD'}
          </Badge>
        );
      case 'estimated_word_count':
      case 'publish_date':
      case 'created_at':
        return value ? (
          <span className="text-xs text-muted-foreground">
            {value}
          </span>
        ) : '-';
      case 'secondary_keywords':
      case 'content_focus':
      case 'keyword_integration_notes':
      case 'related_terms':
        return value ? (
          <div className="text-xs text-foreground break-words leading-relaxed whitespace-pre-wrap">
            {value}
          </div>
        ) : '-';
      case 'detected_industry':
      case 'url':
        return value ? (
          <div className="text-xs text-foreground break-words leading-relaxed">
            {value}
          </div>
        ) : '-';
      default:
        return <span className="text-foreground text-xs">{value || '-'}</span>;
    }
  };

  const totalItems = pillarGroups.length + standaloneContent.length + pillarGroups.reduce((sum, group) => sum + group.childCount, 0);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Content Ideas ({totalItems})
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            AI-generated content recommendations with collapsible pillar page structure
          </p>
        </div>
        {pillarGroups.length > 0 && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAllPillars}
              className="text-xs"
            >
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAllPillars}
              className="text-xs"
            >
              Collapse All
            </Button>
          </div>
        )}
      </div>
      
      {filteredData.length > 0 ? (
        <div className="bg-card rounded-lg border">
            <table className="w-full text-sm table-fixed min-w-[2000px]">
              <thead className="bg-muted/30">
                <tr>
                  {getContentColumns().map(col => (
                    <th key={col.key} className={`px-3 py-2 text-left font-medium text-muted-foreground text-xs ${col.width}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {/* Render Pillar Groups */}
                {pillarGroups.map((group, groupIndex) => (
                  <React.Fragment key={`pillar-group-${groupIndex}`}>
                    {/* Pillar Page Row */}
                    <tr className={`hover:bg-muted/20 transition-colors ${group.childCount > 0 ? 'bg-blue-50/30 dark:bg-blue-950/10' : ''}`}>
                      {getContentColumns().map(col => (
                        <td key={col.key} className="px-3 py-3 text-sm">
                          {contentCellRenderer(
                            group.pillar[col.key], 
                            col, 
                            group.pillar, 
                            true, // isPillar
                            false, // isChild
                            group.pillarId, 
                            group.childCount
                          )}
                        </td>
                      ))}
                    </tr>
                    
                    {/* Child Content Rows (only if expanded) */}
                    {group.isExpanded && group.childContent.map((child, childIndex) => (
                      <tr key={`child-${groupIndex}-${childIndex}`} className="hover:bg-muted/20 transition-colors bg-muted/10">
                        {getContentColumns().map(col => (
                          <td key={col.key} className="px-3 py-3 text-sm">
                            {contentCellRenderer(
                              child[col.key], 
                              col, 
                              child, 
                              false, // isPillar
                              true // isChild
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                
                {/* Render Standalone Content */}
                {standaloneContent.map((item, index) => (
                  <tr key={`standalone-${index}`} className="hover:bg-muted/20 transition-colors">
                    {getContentColumns().map(col => (
                      <td key={col.key} className="px-3 py-3 text-sm">
                        {contentCellRenderer(item[col.key], col, item, false, false)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm mb-4">No content ideas available yet.</p>
          <p className="text-xs mb-6">Start an AI analysis to generate content recommendations.</p>
          {onStartAnalysis && !analysisStatus?.isRunning && (
            <Button
              onClick={onStartAnalysis}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
            >
              <Brain className="h-4 w-4 mr-2" />
              Start AI Analysis
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Keyword Clusters Tab Content - Comprehensive cluster analysis with all database fields
 */
const KeywordClustersTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean; 
  onRefresh: () => void; 
  workspaceId: string;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, workspaceId, onStartAnalysis, analysisStatus }) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const filteredData = useMemo(() => {
    // First apply emergency workspace filtering
    const emergencyFiltered = emergencyFilterWorkspaceData(data, workspaceId, {
      logFiltering: true,
      fieldName: 'keyword_clusters_ai'
    });
    
    return emergencyFiltered.filter(item => {
      const matchesSearch = !searchFilter || 
        item.cluster_name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        item.primary_keyword?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        item.secondary_keywords?.toLowerCase().includes(searchFilter.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [data, searchFilter, statusFilter, categoryFilter, workspaceId]);

  const statuses = useMemo(() => 
    [...new Set(data.map(item => item.status).filter(Boolean))], [data]
  );

  const categories = useMemo(() => 
    [...new Set(data.map(item => item.category).filter(Boolean))], [data]
  );

  const stats = useMemo(() => {
    const totalClusters = data.length;
    const completedClusters = data.filter(item => item.status === 'Send to Article Writer').length;
    const avgKeywordCount = data.length > 0 
      ? data.reduce((sum, item) => {
          const keywords = item.secondary_keywords ? item.secondary_keywords.split(',').length : 0;
          return sum + keywords;
        }, 0) / data.length
      : 0;

    return { totalClusters, completedClusters, avgKeywordCount };
  }, [data]);

  // Helper function to parse and display array/string fields
  const parseArrayField = (field: string | null | undefined): string[] => {
    if (!field) return [];
    
    try {
      // Try parsing as JSON array first
      if (field.trim().startsWith('[') && field.trim().endsWith(']')) {
        return JSON.parse(field);
      }
      // Fallback to comma-separated string
      return field.split(',').map(item => item.trim()).filter(Boolean);
    } catch {
      return field.split(',').map(item => item.trim()).filter(Boolean);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <Network className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Clusters</p>
              <p className="text-xl font-medium">{stats.totalClusters}</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ready for Content</p>
              <p className="text-xl font-medium">{stats.completedClusters}</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 hover:bg-muted/20 transition-colors duration-150 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <BarChart3 className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Keywords</p>
              <p className="text-xl font-medium">{stats.avgKeywordCount.toFixed(1)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="flex-1">
          <Input
            placeholder="Search clusters, keywords..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full border-border/50 focus:border-border"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] border-border/50">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {statuses.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] border-border/50">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
              <Network className="w-5 h-5" />
              Keyword Clusters Analysis
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              AI-generated keyword clusters with semantic groupings and content recommendations
            </p>
          </div>
          <Badge variant="secondary">{filteredData.length} clusters</Badge>
        </div>
        
        {filteredData.length > 0 ? (
          <table className="w-full border-separate border-spacing-0 table-auto">
            <thead>
              <tr className="border-b border-border/30">
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[300px]">Cluster Name</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[180px]">Primary Keyword</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[300px]">Secondary Keywords</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[100px]">Status</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[120px]">Article Type</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[100px]">Category</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[120px]">Content Type</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[200px]">Intent Pattern</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[250px]">Related Terms</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[300px]">Semantic Variations</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[300px]">Question Variations</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-muted-foreground bg-muted/30 min-w-[350px]">Integration Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={index} className="border-b border-border/20 hover:bg-muted/20 transition-colors duration-150">
                  <td className="px-3 py-3 text-sm text-foreground font-medium">
                    <div className="break-words whitespace-pre-wrap leading-relaxed">{item.cluster_name || '-'}</div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-sm text-foreground break-words whitespace-pre-wrap leading-relaxed">
                      {item.primary_keyword || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-sm text-foreground break-words whitespace-pre-wrap leading-relaxed">
                      {item.secondary_keywords ? 
                        parseArrayField(item.secondary_keywords).join(', ') 
                        : '-'
                      }
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <Badge 
                      variant={
                        item.status === 'Send to Article Writer' ? 'default' :
                        item.status === 'In Article Writer' ? 'secondary' :
                        item.status === 'TBD' ? 'outline' : 'outline'
                      }
                      className="text-xs"
                    >
                      {item.status || 'TBD'}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {item.article_type ? (
                      <Badge variant="secondary" className="text-xs">
                        {item.article_type}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {item.category ? (
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          item.category === 'TOFU' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          item.category === 'MOFU' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                          item.category === 'BOFU' ? 'bg-green-50 text-green-700 border-green-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {item.category}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    {item.content_type ? (
                      <Badge variant="outline" className="text-xs">
                        {item.content_type}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-xs text-foreground break-words whitespace-pre-wrap leading-relaxed" title={item.intent_pattern}>
                      {item.intent_pattern || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-xs text-foreground break-words whitespace-pre-wrap leading-relaxed" title={item.related_terms}>
                      {item.related_terms || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-sm text-foreground break-words whitespace-pre-wrap leading-relaxed">
                      {item.semantic_variations ? 
                        parseArrayField(item.semantic_variations).join(', ') 
                        : '-'
                      }
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-sm text-foreground break-words whitespace-pre-wrap leading-relaxed">
                      {item.question_variations ? 
                        parseArrayField(item.question_variations).join(', ') 
                        : '-'
                      }
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-foreground">
                    <div className="text-xs text-foreground break-words whitespace-pre-wrap leading-relaxed" title={item.keyword_integration_notes}>
                      {item.keyword_integration_notes || '-'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <Network className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm mb-4">No keyword clusters available yet.</p>
            <p className="text-xs mb-6">Start an AI analysis to generate keyword clusters.</p>
            {onStartAnalysis && !analysisStatus?.isRunning && (
              <Button
                onClick={onStartAnalysis}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
              >
                <Brain className="h-4 w-4 mr-2" />
                Start AI Analysis
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Semantic Variations Tab Content - Dedicated tab for semantic keyword analysis
 */
const SemanticVariationsTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean; 
  onRefresh: () => void; 
  workspaceId: string;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, workspaceId, onStartAnalysis, analysisStatus }) => {
  
  const filteredData = useMemo(() => {
    // Extract semantic variations data from keyword clusters
    const semanticData: any[] = [];
    
    data.forEach(cluster => {
      if (cluster.semantic_variations && cluster.semantic_variations.trim().length > 0) {
        let variations: string[] = [];
        
        try {
          // Try to parse as JSON array first
          if (cluster.semantic_variations.trim().startsWith('[') && cluster.semantic_variations.trim().endsWith(']')) {
            variations = JSON.parse(cluster.semantic_variations);
          } else {
            // Fallback to comma-separated string
            variations = cluster.semantic_variations.split(',').map((v: string) => v.trim()).filter(Boolean);
          }
        } catch (error) {
          variations = cluster.semantic_variations.split(',').map((v: string) => v.trim()).filter(Boolean);
        }
        
        // Clean up quotes from variations
        variations = variations.map(v => v.replace(/^["']|["']$/g, '').trim()).filter(Boolean);
        
        variations.forEach((variation, index) => {
          semanticData.push({
            id: `${cluster.id}-${index}`,
            primary_keyword: cluster.primary_keyword,
            semantic_variation: variation,
            related_terms: cluster.related_terms,
            cluster_name: cluster.cluster_name,
            intent_pattern: cluster.intent_pattern,
            category: cluster.category,
            created_at: cluster.created_at
          });
        });
      }
    });
    
    return semanticData;
  }, [data, workspaceId]);

  // Column definitions for semantic variations
  const getSemanticColumns = () => [
    { key: 'primary_keyword', label: 'Primary Keyword', width: '200px' },
    { key: 'semantic_variation', label: 'Semantic Variation', width: '300px' },
    { key: 'related_terms', label: 'Related Terms', width: '250px' },
    { key: 'cluster_name', label: 'Cluster Name', width: '200px' },
    { key: 'intent_pattern', label: 'Intent Pattern', width: '180px' },
    { key: 'category', label: 'Category', width: '120px' },
    { key: 'created_at', label: 'Created', width: '120px' }
  ];

  // Cell renderer for semantic variations
  const semanticCellRenderer = (value: any, column: any, row: any) => {
    switch (column.key) {
      case 'primary_keyword':
        return value ? (
          <Badge variant="outline" className="text-xs font-medium">
            {value}
          </Badge>
        ) : null;
      case 'semantic_variation':
        return (
          <div className="font-medium text-foreground break-words leading-normal">
            {value}
          </div>
        );
      case 'cluster_name':
        return value ? (
          <Badge variant="secondary" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'category':
        return value ? (
          <Badge variant="outline" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'related_terms':
      case 'intent_pattern':
        return value ? (
          <div className="text-xs text-foreground break-words leading-relaxed">
            {value}
          </div>
        ) : '-';
      case 'created_at':
        return value ? (
          <span className="text-xs text-muted-foreground">
            {new Date(value).toLocaleDateString()}
          </span>
        ) : '-';
      default:
        return <span className="text-foreground text-xs">{value || '-'}</span>;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Network className="w-5 h-5" />
            Semantic Variations ({filteredData.length})
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Semantic keyword variations and related terms for enhanced keyword research
          </p>
        </div>
      </div>
      
      {filteredData.length > 0 ? (
        <div className="bg-card rounded-lg border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  {getSemanticColumns().map(col => (
                    <th key={col.key} className="px-3 py-2 text-left font-medium text-muted-foreground text-xs" style={{width: col.width}}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData.map((item, index) => (
                  <tr key={index} className="hover:bg-muted/20 transition-colors">
                    {getSemanticColumns().map(col => (
                      <td key={col.key} className="px-3 py-3 text-sm">
                        {semanticCellRenderer(item[col.key], col, item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Network className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm mb-4">No semantic variations available yet.</p>
          <p className="text-xs mb-6">Start an AI analysis to generate semantic keyword variations.</p>
          {onStartAnalysis && !analysisStatus?.isRunning && (
            <Button
              onClick={onStartAnalysis}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
            >
              <Brain className="h-4 w-4 mr-2" />
              Start AI Analysis
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Question Variations Tab Content - Dedicated tab for question variations from clusters
 */
const QuestionVariationsTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean; 
  onRefresh: () => void; 
  workspaceId: string;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, workspaceId, onStartAnalysis, analysisStatus }) => {
  
  const filteredData = useMemo(() => {
    // Extract question variations data from keyword clusters
    const questionsData: any[] = [];
    
    data.forEach(cluster => {
      if (cluster.question_variations && cluster.question_variations.trim().length > 0) {
        let questions: string[] = [];
        
        try {
          // Try to parse as JSON array first
          if (cluster.question_variations.trim().startsWith('[') && cluster.question_variations.trim().endsWith(']')) {
            questions = JSON.parse(cluster.question_variations);
          } else {
            // Fallback to comma-separated string
            questions = cluster.question_variations.split(',').map((q: string) => q.trim()).filter(Boolean);
          }
        } catch (error) {
          questions = cluster.question_variations.split(',').map((q: string) => q.trim()).filter(Boolean);
        }
        
        // Clean up quotes from questions
        questions = questions.map(q => q.replace(/^["']|["']$/g, '').trim()).filter(Boolean);
        
        questions.forEach((question, index) => {
          questionsData.push({
            id: `${cluster.id}-${index}`,
            question: question,
            primary_keyword: cluster.primary_keyword,
            cluster_name: cluster.cluster_name,
            intent_pattern: cluster.intent_pattern,
            category: cluster.category,
            article_type: cluster.article_type,
            content_type: cluster.content_type,
            status: cluster.status,
            created_at: cluster.created_at
          });
        });
      }
    });
    
    return questionsData;
  }, [data, workspaceId]);

  // Column definitions for question variations
  const getQuestionColumns = () => [
    { key: 'question', label: 'Question', width: '400px' },
    { key: 'primary_keyword', label: 'Primary Keyword', width: '200px' },
    { key: 'cluster_name', label: 'Cluster Name', width: '200px' },
    { key: 'intent_pattern', label: 'Intent Pattern', width: '180px' },
    { key: 'category', label: 'Category', width: '120px' },
    { key: 'article_type', label: 'Article Type', width: '120px' },
    { key: 'content_type', label: 'Content Type', width: '160px' },
    { key: 'status', label: 'Status', width: '120px' },
    { key: 'created_at', label: 'Created', width: '120px' }
  ];

  // Cell renderer for question variations
  const questionCellRenderer = (value: any, column: any, row: any) => {
    switch (column.key) {
      case 'question':
        return (
          <div className="font-medium text-foreground break-words leading-normal">
            {value}
          </div>
        );
      case 'primary_keyword':
        return value ? (
          <Badge variant="outline" className="text-xs font-medium">
            {value}
          </Badge>
        ) : null;
      case 'cluster_name':
        return value ? (
          <Badge variant="secondary" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'category':
      case 'content_type':
      case 'article_type':
        return value ? (
          <Badge variant="outline" className="text-xs">
            {value}
          </Badge>
        ) : null;
      case 'status':
        return (
          <Badge variant="outline" className="text-xs">
            {value || 'TBD'}
          </Badge>
        );
      case 'intent_pattern':
        return value ? (
          <div className="text-xs text-foreground break-words leading-relaxed">
            {value}
          </div>
        ) : '-';
      case 'created_at':
        return value ? (
          <span className="text-xs text-muted-foreground">
            {new Date(value).toLocaleDateString()}
          </span>
        ) : '-';
      default:
        return <span className="text-foreground text-xs">{value || '-'}</span>;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Question Variations ({filteredData.length})
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            AI-generated question variations extracted from keyword clusters for content planning
          </p>
        </div>
      </div>
      
      {filteredData.length > 0 ? (
        <div className="bg-card rounded-lg border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  {getQuestionColumns().map(col => (
                    <th key={col.key} className="px-3 py-2 text-left font-medium text-muted-foreground text-xs" style={{width: col.width}}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredData.map((item, index) => (
                  <tr key={index} className="hover:bg-muted/20 transition-colors">
                    {getQuestionColumns().map(col => (
                      <td key={col.key} className="px-3 py-3 text-sm">
                        {questionCellRenderer(item[col.key], col, item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm mb-4">No question variations available yet.</p>
          <p className="text-xs mb-6">Start an AI analysis to generate question variations for your clusters.</p>
          {onStartAnalysis && !analysisStatus?.isRunning && (
            <Button
              onClick={onStartAnalysis}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
            >
              <Brain className="h-4 w-4 mr-2" />
              Start AI Analysis
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Funnel Stages Tab Content - Shows actual funnel data
 */
const FunnelStagesTab: React.FC<{ 
  data: any[]; 
  isLoading: boolean; 
  onRefresh: () => void;
  onStartAnalysis?: () => void;
  analysisStatus?: any;
}> = ({ data, isLoading, onRefresh, onStartAnalysis, analysisStatus }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Funnel Stages
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Keyword mapping to different funnel stages (awareness, consideration, decision) for strategic content planning.
          </p>
        </div>
        <Badge variant="secondary">{data.length} stages</Badge>
      </div>
      
      {data.length > 0 ? (
        <div className="space-y-4">
          {data.map((stage, index) => (
            <div key={index} className="p-4 border-l-4 border-l-purple-500 bg-muted/30 rounded-lg hover:bg-muted/40 transition-colors duration-150">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">{stage.stage}</h4>
                  <p className="text-sm text-muted-foreground">
                    {stage.count} keywords mapped to this funnel stage
                  </p>
                </div>
                <Badge variant="outline" className="text-sm px-3 py-1">
                  {stage.count}
                </Badge>
              </div>
            </div>
          ))}
        </div>      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <TrendingDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm mb-4">No funnel stage data available yet.</p>
          <p className="text-xs mb-6">Start an AI analysis to map keywords to funnel stages.</p>
          {onStartAnalysis && !analysisStatus?.isRunning && (
            <Button
              onClick={onStartAnalysis}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
            >
              <Brain className="h-4 w-4 mr-2" />
              Start AI Analysis
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main AI Analysis Tabs Component with realtime subscriptions
 * 
 * Features:
 * - Real-time data updates via Supabase subscriptions
 * - Instant updates when AI workflows complete
 * - Four analysis tabs: Keyword Categorization, Content Ideas, Keyword Clusters, Funnel Stages
 * - Interactive filtering and search capabilities
 * - Comprehensive status tracking and progress indicators
 * - Error handling with retry functionality
 * - Mobile-responsive design
 */
export const AiAnalysisTabs: React.FC<AiAnalysisTabsProps> = ({ 
  workspaceId, 
  className = '' 
}) => {
  const [activeTab, setActiveTab] = useState('categorization');

  // Enhanced AI analysis data hook with realtime subscriptions
  const {
    data,
    analysisStatus,
    isLoading,
    error,
    lastUpdated,
    startAnalysis,
    stopAnalysis,
    refreshData,
    timeRemaining
  } = useAiAnalysisData({
    workspaceId,
    pollingInterval: 20000, // Kept for compatibility but not used
    analysisTimeoutMinutes: 15,
    enablePolling: true, // Kept for compatibility but not used
    onAnalysisComplete: (completedData) => {
      console.log('AI Analysis completed:', completedData);
      // You can add additional completion handling here
    },
    onAnalysisTimeout: () => {
      console.warn('AI Analysis timed out after 15 minutes');
      // You can add timeout handling here
    }
  });
  // Enhanced workspace contamination detection
  React.useEffect(() => {
    if (workspaceId && !isLoading) {
      // Run contamination detection in the background for debugging
      try {
        const datasets = [
          { name: 'keyword_categorization', data: data.keywordCategorization },
          { name: 'content_briefs', data: data.contentBriefs },
          { name: 'keyword_clusters', data: data.keywordClusters }
        ];
        
        // Simple contamination check without external function
        const hasData = datasets.some(dataset => dataset.data.length > 0);
        
        if (hasData) {
          console.log('[AiAnalysisTabs-AI] Data available for analysis:', {
            workspaceId,
            categorization: data.keywordCategorization.length,
            content: data.contentBriefs.length,
            clusters: data.keywordClusters.length
          });
        }
      } catch (err) {
        console.warn('[AiAnalysisTabs-AI] Error during data validation:', err);
      }
    }
  }, [workspaceId, isLoading, data]);

  // Calculate data availability for status indicator
  const dataAvailable = {
    keywordCategorization: data.keywordCategorization.length > 0,
    contentBriefs: data.contentBriefs.length > 0,
    keywordClusters: data.keywordClusters.length > 0,
    funnelStages: data.funnelStages.length > 0,
  };
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Inline Notification */}
      <div className="p-3 border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100 mb-1 text-sm">
              AI Analysis Information
            </p>
            <p className="text-xs text-blue-800 dark:text-blue-200">
              AI analysis normally takes 5-15 minutes to complete. Results will automatically 
              refresh when processing is finished.
            </p>
          </div>
        </div>
      </div>

      {/* Prominent Call-to-Action when no data available */}
      {!dataAvailable.keywordCategorization && !dataAvailable.contentBriefs && 
       !dataAvailable.keywordClusters && !dataAvailable.funnelStages && 
       !analysisStatus.isRunning && (
        <div className="text-center py-12 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 rounded-xl border border-violet-200 dark:border-violet-800">
          <Brain className="w-16 h-16 mx-auto mb-6 text-violet-600" />
          <h3 className="text-xl font-semibold text-foreground mb-3">Ready to Generate AI Insights</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Transform your keyword data into actionable insights with keyword categorization, 
            content ideas, cluster analysis, and funnel mapping.
          </p>
          <Button
            onClick={startAnalysis}
            size="lg"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-8 py-3 text-base"
          >
            <Brain className="h-5 w-5 mr-2" />
            Start AI Analysis
          </Button>
        </div>
      )}

      {/* Analysis Tabs */}
      <div className="border-b border-border/30 mb-6">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('categorization')}
            className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'categorization'
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
            }`}
          >
            <Brain className="w-4 h-4" />
            <span className="hidden sm:inline">Keyword Categories</span>
            <span className="sm:hidden">Categories</span>
            {data.keywordCategorization.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {data.keywordCategorization.length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('clusters')}
            className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'clusters'
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
            }`}
          >
            <Network className="w-4 h-4" />
            <span className="hidden sm:inline">Keyword Clusters</span>
            <span className="sm:hidden">Clusters</span>
            {dataAvailable.keywordClusters && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {data.keywordClusters.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('content')}
            className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'content'
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            <span className="hidden sm:inline">Content Ideas</span>
            <span className="sm:hidden">Content</span>
            {data.contentBriefs.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {data.contentBriefs.length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('semantic')}
            className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'semantic'
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
            }`}
          >
            <Network className="w-4 h-4" />
            <span className="hidden sm:inline">Semantic Variations</span>
            <span className="sm:hidden">Semantic</span>
            {data.keywordClusters.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {data.keywordClusters.filter(c => c.semantic_variations).length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('questions')}
            className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'questions'
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Question Variations</span>
            <span className="sm:hidden">Questions</span>
            {data.keywordClusters.length > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {data.keywordClusters.filter(c => c.question_variations).length}
              </span>
            )}
          </button>
        </div>
      </div>      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'categorization' && (
          <KeywordCategorizationTab
            data={data.keywordCategorization}
            isLoading={isLoading}
            onRefresh={refreshData}
            workspaceId={workspaceId}
            onStartAnalysis={startAnalysis}
            analysisStatus={analysisStatus}
          />
        )}

        {activeTab === 'content' && (
          <ContentIdeasTab
            data={data.contentBriefs}
            isLoading={isLoading}
            onRefresh={refreshData}
            workspaceId={workspaceId}
            onStartAnalysis={startAnalysis}
            analysisStatus={analysisStatus}
          />
        )}

        {activeTab === 'clusters' && (
          <KeywordClustersTab
            data={data.keywordClusters}
            isLoading={isLoading}
            onRefresh={refreshData}
            workspaceId={workspaceId}
            onStartAnalysis={startAnalysis}
            analysisStatus={analysisStatus}
          />
        )}

        {activeTab === 'semantic' && (
          <SemanticVariationsTab
            data={data.keywordClusters}
            isLoading={isLoading}
            onRefresh={refreshData}
            workspaceId={workspaceId}
            onStartAnalysis={startAnalysis}
            analysisStatus={analysisStatus}
          />
        )}

        {activeTab === 'questions' && (
          <QuestionVariationsTab
            data={data.keywordClusters}
            isLoading={isLoading}
            onRefresh={refreshData}
            workspaceId={workspaceId}
            onStartAnalysis={startAnalysis}
            analysisStatus={analysisStatus}
          />
        )}
      </div>
    </div>
  );
};